// src/modules/inventory/components/InventoryForm.tsx
import React, { useEffect, useState } from "react";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { TextField, Select, MenuItem, Button, FormControl, InputLabel, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

type Supplier = {
  _id: string;
  name: string;
};

type Props = {
  initialData?: any;
  onSubmit: (data: any) => void;
};

const SupplierAddModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreate: (supplier: Supplier) => void;
}> = ({ open, onClose, onCreate }) => {
  const { token } = useAuth();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      // You can expand with full SupplierForm if needed
      const res = await api.post(
        "/suppliers",
        { name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCreate(res.data);
      onClose();
      setName("");
    } catch (e: any) {
      alert(e.response?.data?.message || "Could not add supplier");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white text-gray-800 p-6 rounded-2xl shadow-lg w-full max-w-md">
        <h2 className="text-xl font-bold mb-3">Add Supplier</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <TextField value={name} onChange={(e) => setName(e.target.value)} required placeholder="Supplier Name" label="Supplier Name" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }} />
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outlined" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving || !name}>{saving ? "Saving..." : "Save"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InventoryForm: React.FC<Props> = ({ initialData, onSubmit }) => {
  const isEditMode = Boolean(initialData?.sku);
  const { token } = useAuth();
  // Use light input styling on white card, overriding dark theme inputs
  const lightFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      '& fieldset': { borderColor: '#e5e7eb' }, // gray-200
      '&:hover fieldset': { borderColor: '#cbd5e1' }, // slate-300
      '&.Mui-focused fieldset': { borderColor: '#1e3a8a' }, // brand indigo
    },
    '& .MuiInputBase-input': {
      color: '#0f172a', // slate-900
      '::placeholder': { color: '#94a3b8', opacity: 1 }, // slate-400
    },
    '& .MuiInputLabel-root': {
      color: '#475569', // slate-600
      '&.Mui-focused': { color: '#1e3a8a' },
    },
    '& .MuiSelect-icon': { color: '#475569' },
  } as const;
  const [form, setForm] = useState({
    name: initialData?.name || "",
    sku: initialData?.sku || "",
    category: initialData?.category || "",
    stock: initialData?.stock || 0,
    reorderLevel: initialData?.reorderLevel || 1,
    supplier: initialData?.supplier || "",
    price: initialData?.price || 0,
    costPrice: initialData?.costPrice || 0,
    status: initialData?.status || "active",
  });
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [saving, setSaving] = useState(false);

  // Supplier Search (live search)
  useEffect(() => {
    if (!token) return;
    setLoadingSuppliers(true);
    api
      .get("/inventory/suppliers/all", {
        params: { search: supplierSearch },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => setSuppliers(res.data))
      .finally(() => setLoadingSuppliers(false));
  }, [supplierSearch, token, supplierModalOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: Number(e.target.value) });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    if (!isEditMode && !payload.sku) {
      delete (payload as any).sku;
    }
    onSubmit(payload);
  };

  const handleSupplierCreated = (supplier: Supplier) => {
    setSuppliers(prev => [supplier, ...prev]);
    setForm(f => ({ ...f, supplier: supplier._id }));
  };

  return (
    <form
      className="bg-white text-gray-800 p-6 md:p-8 rounded-2xl shadow space-y-8"
      onSubmit={handleSubmit}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TextField sx={lightFieldSx} name="name" label="Name" value={form.name} onChange={handleChange as any} required fullWidth size="small" variant="outlined" />
        <TextField
          sx={lightFieldSx}
          name="sku"
          label="SKU"
          value={form.sku}
          onChange={handleChange as any}
          required={isEditMode}
          fullWidth
          size="small"
          variant="outlined"
          disabled={!isEditMode}
          InputProps={{ readOnly: !isEditMode }}
          helperText={!isEditMode ? "SKU will be generated automatically when you save." : undefined}
        />
        <TextField sx={lightFieldSx} name="category" label="Category" value={form.category} onChange={handleChange as any} fullWidth size="small" variant="outlined" />
        <FormControl size="small" fullWidth sx={lightFieldSx}>
          <InputLabel id="status-label">Status</InputLabel>
          <Select sx={lightFieldSx} labelId="status-label" id="status" name="status" label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="discontinued">Discontinued</MenuItem>
          </Select>
        </FormControl>
        <TextField sx={lightFieldSx} name="stock" label="Stock" type="number" inputProps={{ min: 0 }} value={form.stock} onChange={handleNumberChange as any} required fullWidth size="small" variant="outlined" />
        <TextField sx={lightFieldSx} name="reorderLevel" label="Reorder Level" type="number" inputProps={{ min: 0 }} value={form.reorderLevel} onChange={handleNumberChange as any} required fullWidth size="small" variant="outlined" />
        <TextField sx={lightFieldSx} name="price" label="Price" type="number" inputProps={{ step: 0.01, min: 0 }} value={form.price} onChange={handleNumberChange as any} required fullWidth size="small" variant="outlined" />
        <TextField sx={lightFieldSx} name="costPrice" label="Cost Price" type="number" inputProps={{ step: 0.01, min: 0 }} value={form.costPrice} onChange={handleNumberChange as any} required fullWidth size="small" variant="outlined" />
      </div>

      <div className="md:col-span-2">
        <label className="block font-medium mb-1">Supplier</label>
        <div className="flex gap-2">
          <FormControl size="small" sx={{ flex: 1, ...lightFieldSx }}>
            <Select
              name="supplier"
              value={form.supplier}
              displayEmpty
              onChange={(e) => setForm({ ...form, supplier: e.target.value })}
              onFocus={() => setSupplierSearch("")}
              sx={lightFieldSx}
            >
              <MenuItem value=""><em>Select supplier</em></MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton color="primary" title="Add Supplier" onClick={() => setSupplierModalOpen(true)}>
            <AddIcon />
          </IconButton>
        </div>
        <TextField
          type="text"
          placeholder="Search supplier..."
          value={supplierSearch}
          onChange={(e) => setSupplierSearch(e.target.value)}
          fullWidth
          size="small"
          sx={{ mt: 1 }}
        />
        {loadingSuppliers && (
          <div className="text-xs text-blue-700 mt-1">Loading suppliers...</div>
        )}
      </div>

      <div className="flex justify-end">
        <Button type="submit" variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>

      <SupplierAddModal
        open={supplierModalOpen}
        onClose={() => setSupplierModalOpen(false)}
        onCreate={handleSupplierCreated}
      />
    </form>
  );
};

export default InventoryForm;
